import React from 'react';
import { MoreHorizontal } from 'react-feather';
import { useTranslation } from 'react-i18next';
import { Button } from '@components-library/Button';
import { PaginationParams } from '@components-shared/table/TablePagination/interfaces';
import { TableAmount } from '@components-shared/table/types';
import { KeyMapKeys, keyMap } from '@locales/lokalise/keyMap';
import { getFormattedNumber } from '@utils/helper';
import cn from 'classnames';

export type TablePaginationProps = {
  paginationParams: PaginationParams;
  perPage: number;
  amounts?: TableAmount[];
  isGettingTotals?: boolean;
  isGettingTotalsFinished?: boolean;
  onGetTotals?: () => void;
};

export const Pagination = ({
  amounts,
  isGettingTotals,
  isGettingTotalsFinished,
  onGetTotals,
  perPage,
  paginationParams,
}: TablePaginationProps) => {
  const { count, truncatePagination, startIndex, changePage } = paginationParams;
  const { i18n } = useTranslation();

  const generatePageRange = (currentPage: number, lastPage: number) => {
    const delta = 2;
    const left = Math.max(1, currentPage - delta);
    const right = Math.min(lastPage - 1, currentPage + delta);

    const range = [];

    for (let i = left; i <= right; i += 1) {
      range.push(i);
    }

    if (currentPage - delta > 1) {
      range.unshift(null);
    }
    if (currentPage + delta < lastPage - 1) {
      range.push(null);
    }

    range.unshift(0);

    if (lastPage !== 0) {
      range.push(lastPage);
    }

    return range;
  };

  const getAdjustedCount = () => {
    if (truncatePagination && count === perPage * 10 + 1) {
      return count - 1;
    }

    return count;
  };

  const renderPages = () => {
    const adjustedCount = getAdjustedCount();
    const lastPage = adjustedCount ? Math.ceil(adjustedCount / perPage) - 1 : 0;
    const currentPage = Math.ceil(startIndex / perPage);

    const pages = generatePageRange(currentPage, lastPage);
    const nextPage = currentPage === lastPage ? lastPage : currentPage + 1;
    const prevPage = currentPage - 1 < 0 ? 0 : currentPage - 1;

    return (
      <div className="pagination">
        <button
          type="button"
          className={cn('pagination__item item--arrow', { disabled: currentPage === 0 })}
          onClick={() => changePage(prevPage)}>
          <span className="pagination__item-icon" />
        </button>
        {pages.map((page, i) => (
          <button
            key={page || i}
            type="button"
            onClick={() => changePage(page || 0)}
            className={cn('pagination__item', {
              active: page === currentPage,
              disabled: page === null,
            })}>
            {page !== null ? (
              page === lastPage && adjustedCount + 1 === count ? (
                `${getFormattedNumber(page + 1)}+`
              ) : (
                page + 1
              )
            ) : (
              <MoreHorizontal size={12} />
            )}
          </button>
        ))}
        <button
          className={cn('pagination__item item--arrow', { disabled: currentPage === lastPage })}
          type="button"
          onClick={() => changePage(nextPage)}>
          <span className="pagination__item-icon icon--next" />
        </button>
      </div>
    );
  };

  const renderTotalCount = () => {
    if (count === 0) {
      return null;
    }

    const currentPage = Math.ceil(startIndex / perPage);
    const from = startIndex + 1;
    const to = Math.min((currentPage + 1) * perPage, count);
    const adjustedCount = getAdjustedCount();

    const formattedFrom = getFormattedNumber(from);
    const formattedTo = getFormattedNumber(to);
    const formattedOf = `${getFormattedNumber(adjustedCount)}${adjustedCount + 1 === count ? '+' : ''}`;

    return (
      <>
        {i18n.t(keyMap.tableTotalCount, {
          from: formattedFrom,
          to: formattedTo,
          of: formattedOf,
        })}
      </>
    );
  };

  const renderTotalAmount = () => {
    if (!onGetTotals) {
      return null;
    }

    return !isGettingTotalsFinished ? (
      <Button
        onClick={onGetTotals}
        className="form__button form__getTotals-button"
        loaderType="mini"
        showButtonPreloader={isGettingTotals}>
        {keyMap.tableShowTotals}
      </Button>
    ) : (
      <ul className="table__pagination-total-amount-list">
        {amounts
          ?.filter((item) => typeof item.value === 'number')
          .map((item, index, items) => {
            const isLastItem = index === items.length - 1;
            const renderedValue = item.renderedValue ?? item.value?.toLocaleString();

            return (
              <React.Fragment key={index}>
                <li>
                  {i18n.t(item.title as KeyMapKeys)}:{' '}
                  <span className="table__pagination-total-amount-list-amount">{renderedValue ?? 'N/A'}</span>
                </li>
                {!isLastItem && <li className="table__pagination-total-amount-list-divider">|</li>}
              </React.Fragment>
            );
          })}
      </ul>
    );
  };

  return (
    <div className="table__pagination">
      <div className="table__pagination-total-amount-info">{renderTotalAmount()}</div>
      <div className="table__pagination-info">{renderTotalCount()}</div>
      {renderPages()}
    </div>
  );
};
